import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import './Dashboard.css';

/**
 * SubscriptionSuccess — landing page after Stripe Checkout completes.
 *
 * Stripe redirects here with `?session_id=cs_…` after a successful payment.
 * The actual access-granting work is done server-side by the stripe-webhook
 * Lambda when Stripe fires `invoice.paid`. That can take a few seconds, so
 * this page polls the user record until access reflects the new subscription.
 */
export const SubscriptionSuccess: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [pollCount, setPollCount] = useState(0);
  const [activated, setActivated] = useState(false);

  // Poll the user record up to ~30 seconds for the webhook-driven activation
  // to land. Most webhooks arrive within 1-3 seconds.
  useEffect(() => {
    if (!sessionId) return;
    if (activated) return;
    if (pollCount > 15) return; // ~30s max (15 × 2s)

    const newSubActive =
      user?.subscriptionStatus === 'active' ||
      Boolean((user as any)?.activeProducts?.length);

    if (newSubActive && pollCount > 0) {
      setActivated(true);
      return;
    }

    const t = setTimeout(async () => {
      try {
        if (typeof refreshUser === 'function') {
          await refreshUser();
        }
      } catch {
        /* ignore — keep polling */
      }
      setPollCount((c) => c + 1);
    }, 2000);
    return () => clearTimeout(t);
  }, [pollCount, sessionId, user, refreshUser, activated]);

  if (!sessionId) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: '640px', margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
          <h1 style={{ color: '#fff' }}>Missing session reference</h1>
          <p style={{ color: '#94a3b8', marginTop: '1rem' }}>
            We couldn't find your checkout session. If you just completed a payment,
            please refresh your Profile page to confirm activation.
          </p>
          <button
            onClick={() => navigate('/profile')}
            style={btnPrimary}
          >
            Go to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: '640px', margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
        {activated ? (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#10003;</div>
            <h1 style={{ color: '#4ade80', marginBottom: '1rem' }}>You're in.</h1>
            <p style={{ color: '#cbd5e1', fontSize: '1.05rem', marginBottom: '0.5rem' }}>
              Your subscription is active. A receipt has been emailed to you by Stripe.
            </p>
            <p style={{ color: '#94a3b8', marginBottom: '2rem', fontSize: '0.9rem' }}>
              You can manage your subscription at any time from your Profile page.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/dashboard')} style={btnPrimary}>Go to Dashboard</button>
              <button onClick={() => navigate('/profile')} style={btnSecondary}>View Profile</button>
            </div>
          </>
        ) : pollCount > 15 ? (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>&#9203;</div>
            <h1 style={{ color: '#fbbf24', marginBottom: '1rem' }}>Payment received — activation pending</h1>
            <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>
              Your payment went through, but we haven't seen the activation come back from
              Stripe yet. This can take up to a minute under load.
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '2rem' }}>
              Refresh your Profile page in 30-60 seconds. If access doesn't appear, contact{' '}
              <a href="mailto:support@wealthfinancialsystems.com" style={{ color: '#60a5fa' }}>
                support
              </a>{' '}
              with this session ID:{' '}
              <code style={{ color: '#cbd5e1', background: 'rgba(255,255,255,0.05)', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>
                {sessionId}
              </code>
            </p>
            <button onClick={() => navigate('/profile')} style={btnPrimary}>Go to Profile</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>&#8987;</div>
            <h1 style={{ color: '#fff', marginBottom: '1rem' }}>Activating your subscription…</h1>
            <p style={{ color: '#94a3b8' }}>
              Stripe just confirmed your payment. We're flipping on your access now —
              this usually takes 2-5 seconds.
            </p>
            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '40px', height: '40px',
                border: '3px solid rgba(255,255,255,0.1)',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}
      </div>
    </div>
  );
};

const btnPrimary: React.CSSProperties = {
  padding: '0.7rem 1.5rem',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.95rem',
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  backgroundColor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.15)',
};

export default SubscriptionSuccess;
